import UniversalPageLayout from "../components/BaseComponents/UniversalPageLayout/UniversalPageLayout";
import routes from "../components/Router/routes";
import useUserData from "../hooks/useUserData";
import { useContext, useEffect, useState } from "react";
import { Button, InlineAlert, LoadingSpinner } from "../components/BaseComponents";
import CanonicalModal from "../components/BaseComponents/CanonicalModal/CanonicalModal";
import AccordionSection from "../components/BaseComponents/AccordionSection";
import { FICTITIOUS_HINTS, SUFFIX_CLASS } from "../components/OrganisationSwitcher/OrganisationSwitcher";
import UserSettingsSection from "../components/UserSettingsSection/UserSettingsSection";
import ConnectOrganisationSetting from "../components/ConnectOrganisationSetting/ConnectOrganisationSetting";
import { SettingsGroup, SettingsItem, SettingsSubItem } from "../components/SettingsLayout/SettingsLayout";
import { isSampleState } from "../lib/sampleState";
import { getApiErrorMessage, getCSRFToken, getCurrencySymbol } from "../lib/utils";
import SimpleSortTable from "../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import useApi from "../hooks/useApi";
import useCleanup from "../hooks/useCleanup";
import { userContext } from "../context/UserContext";
import useIsViewOnly from "../hooks/useIsViewOnly";

function CompanyUsers() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any[]>([]);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);

    const api = useApi();

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');

        const run = async () => {
            try {
                const result = await api.get('company/users')
                if (!cancelled) {
                    setData(result.data.users);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(getApiErrorMessage(err, 'Could not load users. Please try again.'));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [retry])

    const columns = [
        {
            id: 'email',
            // sortable: true,
            label: 'Email',
        },
        {
            id: 'fullName',
            // sortable: true,
            label: 'Name',
        },
        {
            id: 'role',
            // sortable: true,
            label: 'Role',
        },
    ]

    // An empty group still shows its table, with "None" in the first row.
    const rowsOrNone = (users: any[]) => users.length > 0 ? users : [{ _id: 'none', email: 'None' }];

    return (
        <SettingsItem title="Users">
            {loading ? <LoadingSpinner /> : error ? (
                <>
                    <InlineAlert type="error" className="">{error}</InlineAlert>
                    <Button type="Secondary" size="sm" text="Retry loading users" onClick={() => setRetry((count) => count + 1)} />
                </>
            ) : (
                <div className="space-y-6">
                    <SettingsSubItem title="Active">
                        <SimpleSortTable
                            data={rowsOrNone(data.filter(user => !user.locked))}
                            columns={columns}
                        />
                    </SettingsSubItem>
                    <SettingsSubItem title="Locked">
                        <SimpleSortTable
                            data={rowsOrNone(data.filter(user => user.locked))}
                            columns={columns}
                        />
                    </SettingsSubItem>
                </div>
            )}
        </SettingsItem>
    )

}

function ExportData() {
    const userData = useUserData();
    const assumedCompanyType = userData.access?.assumedCompanyType;
    // Mirrors UserContext's checkAccess('company.accounting') source of truth - the tier gate
    // behind the "Purchases" nav link on CompanyDashboard - read directly here (rather than via
    // checkAccess) since this file's tests mock useUserData only, with no userContext.Provider.
    const hasAccountingAccess = userData.access?.company?.permissions?.accounting;
    const disabledForAssumedAccess = Boolean(assumedCompanyType);
    const disabledForTier = !hasAccountingAccess;

    return (
        <SettingsItem title="Export data">
            <div>
                <Button
                    type="Outlined"
                    size="sm"
                    text="Export data"
                    disabled={disabledForAssumedAccess || disabledForTier}
                    onClick={() => {
                        window.location.href = `${process.env.REACT_APP_BASE_API}/dataExport`
                    }}
                />
            </div>
            {/* The upgrade note only explains the tier gate - showing it while assuming another
                organisation (which is also disabled, but for an unrelated reason) would be misleading. */}
            {!disabledForAssumedAccess && disabledForTier && (
                <div className="text-charcoal-100 text-sm">
                    Upgrade your organisation to a Standard or Premium tier to enable this feature.
                </div>
            )}
        </SettingsItem>
    )
}

function AdminEntryPermission() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [loadError, setLoadError] = useState('');
    const [actionError, setActionError] = useState('');
    const [retry, setRetry] = useState(0);
    const api = useApi();
    const assumedCompanyType = useUserData().access?.assumedCompanyType;
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError('');
        const run = async () => {
            try {
                const result = await api.get('/companySettings')
                if (!cancelled) {
                    setData(result.data);
                }
            } catch (err) {
                if (!cancelled) {
                    setLoadError(getApiErrorMessage(err, 'Could not load support access settings. Please try again.'));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        run();

        return () => {
            cancelled = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [retry]);

    const onChange = async (enabled: boolean) => {
        setLoading(true);
        setActionError('');
        try {
            const result = await api.put('/companySettings', {
                _csrf: await getCSRFToken(),
                settings: {
                    adminEntryPermission: enabled,
                }
            })
            setData(result.data)
        } catch (err) {
            setActionError(getApiErrorMessage(err, 'Could not update support access. Please try again.'));
        } finally {
            setLoading(false);
        }
    }

    const enabled = data?.adminEntryPermission;

    const warning = "You have enabled access for the Neoni Support Team, who can currently see all your organisation's data.";
    const loadingOrError = loading ? <LoadingSpinner /> : (
        <>
            <InlineAlert type="error" className="">{loadError}</InlineAlert>
            <Button type="Secondary" size="sm" text="Retry loading support access" onClick={() => setRetry((count) => count + 1)} />
        </>
    );

    if (assumedCompanyType) {
        return (
            <SettingsItem
                title="Permit Neoni Support Team access"
                description={loading || loadError || enabled ? undefined : 'Neoni Support Team access is not enabled.'}
            >
                {loading || loadError ? loadingOrError : (
                    enabled && (
                        <InlineAlert type="error" className="" data-testid="neoni-access-warning">
                            {warning} Only members of this organisation can change this setting.
                        </InlineAlert>
                    )
                )}
            </SettingsItem>
        )
    }
    return (
        <SettingsItem title="Permit Neoni Support Team access">
            {loading || loadError ? loadingOrError : (
                <>
                    {actionError && <InlineAlert type="error" className="">{actionError}</InlineAlert>}
                    {enabled ? (
                        <>
                            <InlineAlert type="error" className="" data-testid="neoni-access-warning">
                                {warning} You can disable this access below.
                            </InlineAlert>
                            <div>
                                <Button requiresWrite type="Outlined" size="sm" text="Disable" onClick={() => onChange(false)} />
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <Button requiresWrite type="Outlined" size="sm" text="Enable" onClick={() => onChange(true)} />
                            </div>
                            <div className="text-error-400 text-sm" data-testid="neoni-access-danger-zone">
                                DANGER ZONE: This will enable the Neoni Support Team to view and access all your data. Only proceed if you are comfortable with this.
                            </div>
                        </>
                    )}
                </>
            )}
        </SettingsItem>
    )

}

function ResetSampleOrganisation() {
    const userData = useUserData();
    const { fetchUserData } = useContext(userContext);
    const api = useApi();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!isSampleState(userData) || userData.access?.assumedCompanyType) {
        return null;
    }

    const onConfirm = async () => {
        setBusy(true);
        setError('');
        try {
            await api.post('/sample-organisation/reset', { _csrf: await getCSRFToken() });
            await fetchUserData?.();
            setOpen(false);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not reset your sample organisation. Please try again.'));
        } finally {
            setBusy(false);
        }
    }

    return (
        <SettingsItem title="Sample organisation">
            <div>
                <Button
                    type="Outlined"
                    size="sm"
                    text="Reset sample organisation"
                    onClick={() => setOpen(true)}
                />
            </div>
            <div className="text-charcoal-100 text-sm">Restore your sample organisation to its original data. Any changes you made to it are lost.</div>
            <CanonicalModal
                open={open}
                onClose={() => setOpen(false)}
                title="Reset sample organisation"
                subtitle={userData.companyName}
                actions={
                    <>
                        <Button
                            type="Primary"
                            text={busy ? 'Resetting...' : 'Reset'}
                            disabled={busy}
                            onClick={onConfirm}
                            additionalClass="mr-5"
                        />
                        <Button type="Outlined" text="Cancel" onClick={() => setOpen(false)} />
                    </>
                }
            >
                <p className="text-white mt-8">
                    This deletes your sample organisation and its data and replaces it with a fresh copy. Other users are not affected.
                </p>
                {error && <InlineAlert type="error" className="mt-4">{error}</InlineAlert>}
            </CanonicalModal>
        </SettingsItem>
    )
}

function CompanySettings() {
    const userData = useUserData();
    const symbol = userData.currencyCode ? getCurrencySymbol(userData.currencyCode) : undefined;
    const currencyLabel = symbol ? `${userData.currencyCode} (${symbol})` : userData.currencyCode;
    const fictitiousKind = userData.access?.fictitiousKind;
    const organisationTitle = userData.companyName ? (
        <>
            Organisation: {userData.companyName}
            {fictitiousKind && <span className={SUFFIX_CLASS}> ({FICTITIOUS_HINTS[fictitiousKind]})</span>}
        </>
    ) : 'Organisation';
    return (
		<UniversalPageLayout
			documentTitle={routes.companySettings.name}
			heading={{ title: 'Account Settings' }}
		>
			<div className="text-white">
				<UserSettingsSection />
				<AccordionSection
					id="settings-organisation"
					title={organisationTitle}
					defaultOpen
				>
					<SettingsGroup>
						<ConnectOrganisationSetting />
						<SettingsItem title="Currency">
							<div className="text-white">{currencyLabel}</div>
						</SettingsItem>
						<ResetSampleOrganisation />
						<CompanyUsers />
						<ExportData />
						<AdminEntryPermission />
					</SettingsGroup>
				</AccordionSection>
			</div>
		</UniversalPageLayout>
    )
}

export default CompanySettings;
